'use client'

import { useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { calcHref, formatTry, type CalcCurrency } from '@/lib/currency-calc'
import { CALC_CURRENCIES } from '@/lib/currency-calc'

export type CalculatorRates = Partial<Record<string, number>>

type CurrencyCalculatorProps = {
  /** urlSlug → 1 birimin TL karşılığı */
  unitRates: CalculatorRates
  initialCurrencySlug?: string
  initialAmount?: number
  className?: string
}

function parseAmountInput(raw: string): number | null {
  // tr girişleri: "8.877", "8877", "8877,5"
  const cleaned = raw.trim().replace(/\./g, '').replace(',', '.')
  if (!cleaned) return null
  const n = Number(cleaned)
  return Number.isFinite(n) && n > 0 ? n : null
}

export default function CurrencyCalculator({
  unitRates,
  initialCurrencySlug = 'dolar',
  initialAmount = 1,
  className = '',
}: CurrencyCalculatorProps) {
  const router = useRouter()
  const available = useMemo(
    () => CALC_CURRENCIES.filter((c) => (unitRates[c.urlSlug] ?? 0) > 0),
    [unitRates],
  )
  const fallback = available[0] ?? CALC_CURRENCIES[0]
  const [currency, setCurrency] = useState<CalcCurrency>(
    () => available.find((c) => c.urlSlug === initialCurrencySlug) ?? fallback,
  )
  const [amountText, setAmountText] = useState(() =>
    new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 8 }).format(initialAmount),
  )

  const amount = parseAmountInput(amountText)
  const unitTry = unitRates[currency.urlSlug] ?? 0
  const totalTry = amount != null && unitTry > 0 ? amount * unitTry : null

  const goToPage = () => {
    if (amount == null || !Number.isInteger(amount) || amount < 1 || amount > 999_999_999_999) return
    router.push(calcHref(amount, currency))
  }

  return (
    <section
      className={`rounded-xl border border-gray-200 bg-white p-4 sm:p-5 dark:border-slate-800 dark:bg-slate-950 ${className}`}
    >
      <h2 className="text-base font-black text-gray-900 dark:text-gray-100">Döviz &amp; Kripto Hesaplama</h2>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Güncel kurlarla anında Türk lirası karşılığını hesaplayın.
      </p>

      <div className="mt-3 flex flex-wrap gap-1.5" role="tablist" aria-label="Kur seçimi">
        {available.map((c) => {
          const active = c.urlSlug === currency.urlSlug
          return (
            <button
              key={c.urlSlug}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setCurrency(c)}
              className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${
                active
                  ? 'border-red-600 bg-red-600 text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-red-300 hover:text-red-700 dark:border-slate-700 dark:bg-slate-900 dark:text-gray-200'
              }`}
            >
              {c.name}
            </button>
          )
        })}
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="flex-1">
          <span className="mb-1 block text-xs font-semibold text-gray-500 dark:text-gray-400">
            Miktar ({currency.symbol})
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={amountText}
            onChange={(e) => setAmountText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') goToPage()
            }}
            placeholder="Örn. 100"
            className="w-full rounded-md border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold tabular-nums text-gray-900 outline-none focus:ring-2 focus:ring-red-600/30 dark:border-slate-700 dark:bg-slate-900 dark:text-gray-100"
            aria-label={`${currency.name} miktarı`}
          />
        </label>
        <button
          type="button"
          onClick={goToPage}
          className="rounded-md bg-red-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-red-700"
        >
          Hesapla
        </button>
      </div>

      <div
        className="mt-4 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/60"
        aria-live="polite"
      >
        {totalTry != null && amount != null ? (
          <p className="text-sm text-gray-700 dark:text-gray-200">
            <strong className="font-black tabular-nums">
              {new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 8 }).format(amount)} {currency.name}
            </strong>
            <span className="mx-1.5 text-gray-400">=</span>
            <strong className="font-black tabular-nums text-red-700 dark:text-red-400">
              {formatTry(totalTry)} TL
            </strong>
          </p>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Geçerli bir miktar girin — sonuç anında hesaplanır.
          </p>
        )}
        {unitTry > 0 ? (
          <p className="mt-1 text-xs tabular-nums text-gray-500 dark:text-gray-400">
            1 {currency.name} = {formatTry(unitTry, unitTry < 1 ? 4 : 2)} TL
          </p>
        ) : null}
      </div>
    </section>
  )
}
